import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import {  faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClearableSearch from '../../../../uiControls/ClearableSearch';
import { ApplicableTableSettings, codesAesScheduleBList } from '../../../../tableSettings';
import useFastStorage from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import ApplicationContext from '../../../../ApplicationContext';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import { Search } from '../../../../filtering';
import { Portal } from 'react-portal';
import { CodeScheduleBListItem } from '../../../../fms.data/models/CodeScheduleBListItem';
import { JobNames } from '../../../../framework.core/JobNames';
import { ApiResult, InsertResult } from '../../../../framework.core/data/ApiResult';
import { SQLType } from '../../../../framework.core/data/SQLInfo';
import { FmsSecTask } from '../../../../fms.core/constants';
import GreenButton from '../../../../ui/GreenButton';
import EditableTable, { CellData, TableCellAligns, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';
import TextBox from '../../../../ui/TextBox';
import { checkTabIsDirty, sortingListData } from '../../ReferenceFiles';
import { duplicateCell, findSecurityTask } from '../../../../util';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import CloseButton from '../../../../ui/CloseButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { ListOrderBy } from '../../../common/AbstractListView';
import { RefreshIconButton2 } from '../../../../ui/IconButton';

function CodeRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
    return (
      <div style={{marginTop: '0px'}}>
        <TextBox textId={`ifms-schbcode-${row.index}`} maxLength={12} value={data[row.index].code} onChange={(e) => {
          changeLineItem(row.index, {code: e.target.value, isUpdate: true})
          }} />
      </div>
    );
}

function DescriptionRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
    return (
      <div style={{marginTop: '0px'}}>
        <TextBox textId={`ifms-schdescription-${row.index}`} maxLength={50} value={data[row.index].sdescription} onChange={(e) => {
        changeLineItem(row.index, {sdescription: e.target.value, isUpdate: true})
          }} />
      </div>
    );
}

function Unit1Renderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
    return (
      <div style={{marginTop: '0px'}}>
        <TextBox textId={`ifms-schunit1-${row.index}`} maxLength={3} value={data[row.index].unit1} onChange={(e) => {
        changeLineItem(row.index, {unit1: e.target.value, isUpdate: true})
          }} />
      </div>
    );
}

function Unit2Renderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
    return (
      <div style={{marginTop: '0px'}}>
        <TextBox textId={`ifms-schunit2-${row.index}`} maxLength={3} value={data[row.index].unit2} onChange={(e) => {
         changeLineItem(row.index, {unit2: e.target.value, isUpdate: true})
          }} />
      </div>
    );
}

class EditableCodeExportInfoListItem extends CodeScheduleBListItem {
  isNew: boolean = false;
  isDelete: boolean = false;
  isUpdate: boolean = false;
}

function formatElement(data:any) {
  const result = new EditableCodeExportInfoListItem();
  result.code = data.code;
  result.sdescription = data.sdescription;
  result.unit1 = data.unit1;
  result.unit2 = data.unit2;
  result.cid = data.cid;
  result.isNew = false;
  result.isDelete = false;

  return result;
}

function formatData(data:Array<any>) {
  return data.map(d => formatElement(d));
}

function Scheduleb() {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
    //ifmsReferenceFilesCancel: () => {},
  }});
  useFunctionKeys({ onSave: handleSaveAndClose });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const systemSettings = useSystemSettings();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  
  const [data, _setData, removeData, /*dataRef*/] = useFastStorage(`ifms-referenceFiles-scheduleb-list`, []);

  const savedDataRef = useRef<Array<CodeScheduleBListItem>>([]);
  const oriData = useRef<CodeScheduleBListItem[]>([]);

  const [orderBy, setOrderBy] = useFastStorage(`ifms-referenceFiles-scheduleb-orderby`, {
    general1: 'code',
    pageSize: systemSettings.defaultPageSize,
    searchConditions: [],
    ...getDefaultOrderBy(),
  });
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeScheduleB);
  const [focusFirstCell, setFocusFirstCell] = useState({ dataLength: 0, focus: false });

  const listItemType = 'CodeScheduleBListItem';

  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'code',
      orderByAscending: true,
    };
  }

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }
  
  async function cancelOperation(){
    setHasChanges(false);
    removeData();
    return true;
  }

  function setData(d: Array<any>, callByFetchData: boolean = false) {
    setFocusFirstCell({ dataLength: d.length, focus: callByFetchData });
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeScheduleBListItem, oriData.current, d));
  }

  const tableHeight = 520;

  async function fetchData(api:any, search:Search) {
    setLoading(true);

    const searchModel = CodeScheduleBListItem.calculateSearchParameters(search);
    const apiResult = await api.getListItems(JobNames.ScheduleB,
      listItemType,
      searchModel, 1, search.pageSize);
    const data = apiResult && apiResult.entities ? apiResult.entities : [];

    oriData.current = formatData(data);
    setData(formatData(data), true);
    savedDataRef.current = data;

    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    async function executeAsyncFunc() {
      if (data.length === 0)
        await reload();
      else{
        oriData.current = _.cloneDeep(data);
        savedDataRef.current = _.cloneDeep(data);
      }
    }
    executeAsyncFunc();
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function reload() {
    setOrderBy(getDefaultOrderBy());

    await fetchData(appContext.api, orderBy);
  }

  function onHeaderClick(header: any) {
    // if (ediRef.current !== -1) {
    //   return;
    // }

    //let newOrderBy = { ...orderBy, orderByAscending: !orderBy.orderByAscending };

    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }

      removeData();
      sortingListData(CodeScheduleBListItem, data, setData, orderBy);
    }

    // const sortResult = sortSettingListData(data ?? [], newOrderBy, codesAesScheduleBList.columns);

    // if (sortResult.sorted) {
    //   setData(sortResult.data);
    // }

  }

  // async function requestOperationCancel() {
  //   const doCancel = await showGlobalYesNoDialog(<p>Are you sure you want to cancel Edit operation</p>);
  //   if (doCancel) {
  //     cancelOperation(false)
  //   } else {
  //     setTimeout(() => {
  //       selectTableRow(ediRef.current);
  //     }, 80);
  //   }
  // }

  // async function onRowIndexChanged(rowIndex:number) {
  //   if (rowIndex < 0 && ediRef.current >= 0) {
  //     setTimeout(() => {
  //       selectTableRow(ediRef.current);
  //     }, 80)
  //     return;
  //   }

  //   if (ediRef.current >= 0 && ediRef.current !== rowIndex) {
  //     await requestOperationCancel();
  //   }
  // }

  // function onRowSelect(rowIndex:number) {
  //   if (rowIndex >= 0 && rowIndex < dataRef.current.length && _.isEmpty(inlineElement) && (listSec?.grantEdit || listSec?.grantDel)) {
  //     setHasChanges(true);
  //     inlineElement = {...dataRef.current[rowIndex], isNew: false};
  //     setEi(rowIndex);
  //   }
  // }

  // function selectForNew() {
  //   inlineElement = {
  //     code: '', sdescription: '', unit1: '', unit2: '', isNew: true,
  //   };
  //   data.unshift(inlineElement);
  //   setData(data);
  //   setEi(0);
  //   setHasChanges(true);
  // }

  // function cancelOperation(leaveNew:boolean=false) {
  //   setHasChanges(false);

  //   //clearTableSelection();
  //   setEi(-1);

  //   if (!leaveNew && inlineElement.isNew) {
  //     dataRef.current.splice(0, 1);
  //     setData(dataRef.current);
  //   }

  //   //inlineElement = {};
  // }

  function deleteEntity(index:number): boolean {
    if(!data){
      return false;
    }

    const element = data[index];
    if(!element.isNew){
      let deleteRow = savedDataRef.current.find(x=>x.code === element.code);
      if(deleteRow){
        deleteRow.isDelete = true;
      }
    }

    data.splice(index, 1);
    setData(data);
    return true;   
  }


  async function save() {
    if (!data) {
      return;
    }

    let validData = data.filter((x:any) => x.code);

    let errMsg = '';
    const dataToSave = validData.filter((x: any) => x.isNew || x.hasBeenChanged === true);
    const dataToDelete = savedDataRef.current.filter((x) => x.isDelete);
    if(dataToSave.length === 0 && dataToDelete.length === 0) {
      if (hasChanges) reload();
      return;
    }
    setLoading(true);

    for (let i = 0; i < dataToDelete.length; ++i) {
      const s = dataToDelete[i];
      const apiResult: ApiResult<InsertResult> = await appContext.api.saveListItem(listItemType, SQLType.Delete, _.omit(s, ['isDelete']));
      if (!apiResult || !apiResult.success)
        errMsg = apiResult?.message ?? constants.SAVE_ERROR_MESSAGE;
    }

    if (dataToSave.length > 0) {
      const isDuplicate = await duplicateCell(dataToSave, data, appContext, listItemType, showGlobalOkDialog, JobNames.ScheduleB, 'code');
      if(isDuplicate){
        setLoading(false);
        return;
      }
      const cidData = dataToSave.map((a: CodeScheduleBListItem) =>a.cid?.toLowerCase()) as Array<String>;

      const secondSave = dataToSave.filter((x: CodeScheduleBListItem) => cidData.includes(x.code?.toLowerCase()));
      const firstSave = dataToSave.filter((x: CodeScheduleBListItem) => !secondSave.includes(x));

      if (firstSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, firstSave);
        if (!apiResult || !apiResult.success)
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);

        else {
          firstSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.code === x.code);
            if (index >= 0) {
              data[index].cid = x.code;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
      if (secondSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, secondSave);
        if (!apiResult || !apiResult.success)
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);

        else {
          secondSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.code === x.code);
            if (index >= 0) {
              data[index].cid = x.code;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
    }

if(errMsg) {
  await showGlobalOkDialog(<p>{errMsg}</p>);
  setLoading(false);
  return;
}    

// setData(data);
// savedDataRef.current = _.cloneDeep(data);
// oriData.current = _.cloneDeep(data);

// setLoading(false);
// setHasChanges(false);
reload();

await showGlobalOkDialog(<p>The data has been saved</p>);
}

  function applyToLineItem(rowIndex: number, change: Partial<EditableCodeExportInfoListItem>|null) {
    if (!change || !data) {
      return;
    }
    const newData = [...data];
    const checkData = data[rowIndex];
    if (!change.isNew && (change.code !== checkData.code || change.sdescription !== checkData.sdescription ||
      change.unit1 !== checkData.unit1 || change.unit2 !== checkData.unit2)) {
    _.merge(newData[rowIndex], {..._.omit(change, ['isNew']), hasBeenChanged: true});
    setData(newData);
    //setHasChanges(true);
      }
  }

  function addLineItem() {
    const newRow = new EditableCodeExportInfoListItem();
    newRow.isNew = true;

    return {
      setDataFunc: (list: any) => setData(list),
      curListData: data,
      newData: newRow
    };
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-aes-scheduleb-list',
    selectable: true,
    tableName: 'scheduleBList',
    ...codesAesScheduleBList,
    height: `${tableHeight}px`,
    data: (data ?? []).map((x:any, i:number) => ({...x, indicator: (i + 1).toString(), changeLineItem: applyToLineItem})),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'code',  EditCell: CodeRenderer },
      { accessor: 'sdescription',  EditCell: DescriptionRenderer },
      { accessor: 'unit1',  EditCell: Unit1Renderer},
      { accessor: 'unit2', EditCell: Unit2Renderer },
      //{ accessor: 'action', Cell: ActionRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'code')
        return { type: TableCellTypes.Text, value: data[rowIndex].code };
      else if (accessor === 'sdescription')
       return { type: TableCellTypes.Text, value: data[rowIndex].sdescription };
      else if (accessor === 'unit1')
        return { type: TableCellTypes.Text, value: data[rowIndex].unit1, align: TableCellAligns.Center};
      else if (accessor === 'unit2')
        return { type: TableCellTypes.Text, value: data[rowIndex].unit2, align: TableCellAligns.Center};
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
    },
    
    orderBy: [orderBy],
    onHeaderClick,
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
    //useTableHelperText: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  return (
    <div style={{marginTop: '40px'}}>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', width:'100px'}}>
          <div className="ifms-common-bar-element-center">
            <GreenButton size="regular" disabled={loading} onClick={save}><span style={{ textDecoration: 'underline' }}>S</span>ave</GreenButton>
          </div>
          <div style={{margin:'auto'}}></div>
          <div className="ifms-common-bar-element-center">
            <CloseButton onlyIcon name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '864px' }}>
        <div style={{ width: '320px', fontSize: '11px', fontWeight: 'bold' }}>
          <ClearableSearch
            initialValue={orderBy?.generalText1 || ''}
            onSearch={generalText1 => {
              setOrderBy({
                general1: generalText1 ? orderBy?.orderBy : null,
                generalText1,
                searchConditions: generalText1
                  ? [{ fieldName: orderBy?.orderBy, condition: 2 }]
                  : [],
              });
              reload();
            }}
          />
        </div>
        <div style={{ marginLeft: 'auto' }}></div>
        <div style={{ position: 'relative', top: '-4px' }}>
          {//<SecondaryButton name='codes-scheduleb-add-button' size="regular" disabled={!canBeAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; 
          }
          {/* <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton> */}
          <RefreshIconButton2 name='list-screen-refresh' onClick={() => { reload() }} disabled={loading} data-tooltip="Refresh"/>
        </div>
      </div>
      {/* </div> */}
      <div style={{ width: '864px' }}>
        {/* <SmartTable {...tableOptions} /> */}
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          tableOptions={tableOptions}
          deleteLine={deleteEntity}
          addLine={addLineItem}
        />
      </div>
      {/* 250721.inhyung.issue#2570. Table 하단에 text 추가. */}
      <div style={{ marginTop: '6px', fontSize: '14px', color: '#000000' }}>
      To add item, press "Insert" Key in any row.<br />
      To delete item, press "Delete" Key on selected row.
    </div>
    </div>
  );
}

export default Scheduleb;
